import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Formik, Form } from 'formik';
import { compareVersions } from 'compare-versions';
import {
  useTranslator,
  useAppContext,
  mealPlaceholderImage,
  useToast,
  callNativeMobileApp,
  formatCalories,
  getCountry,
  cloudflareImage,
  FormikInput as Input,
  withMobileAppMode,
} from '@front-activerse/common-components';
import {
  Container,
  Row,
  Col,
  Button,
  Spinner,
  Modal,
  ModalBody,
  ModalHeader,
  Table,
} from 'reactstrap';
import Layout from '../../Layout/Layout';
import TitleBar from '../../Layout/TitleBar';
import MeasurementUnits from '../../Layout/MeasurementUnits';
import validationSchema from './validationSchema';
import { useNavigate, Link, useOutletContext } from 'react-router-dom';
import { TrackerAddMealProps, Ingredient } from '../../Tracker/types';
import { useGlobalIngredients } from '../../Tracker/hooks/useGlobalIngredients';
import { useGlobalIngredientsEdit } from '../../Tracker/hooks/useGlobalIngredientsEdit';
import FriscoButton from '../../MealPlan/MealView/components/FriscoButton';

const STORAGE_KEY_SHOULD_USE_DATA = 'tracker_should_use_data_from_session_storage';

const TrackerEditMeal: React.FC<TrackerAddMealProps> = ({
  mealData,
  editMeal,
  removeMeal,
  saveAsUserMeal,
  mobileApp,
  date,
  mode,
}) => {
  const { t, language } = useTranslator();
  const { toastError, toastSuccess } = useToast();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const [preloadInstacart, setPreloadInstacart] = useState(false);
  const [showAiModal, setShowAiModal] = useState(false);
  const toggleAiModal = () => {
    setShowAiModal(!showAiModal);
  };

  // Read flag directly on each render - simplest and most reliable approach
  const shouldUseDataFromSessionStorage =
    sessionStorage.getItem(STORAGE_KEY_SHOULD_USE_DATA) === 'true';

  const {
    consts: { APP_BRAND, FRISCO, INSTACART_FIREBASE_FUNCTION, INSTACART_URL },
    images: {
      editPrimaryIcon,
      aiBadgeTiny,
      infoIcon,
      addToTrackerIcon,
      instacartLogo,
      yourMealsRemoveIcon,
      addPlusIcon,
    },
  } = useAppContext();

  const { trackFirebaseEvent } = useOutletContext<{
    trackFirebaseEvent: (eventName: string, eventPayload: object) => void;
  }>();

  const inputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const { ingredients: localIngredients, updateIngredients } = useGlobalIngredients(
    mealData.ingredients
  );

  const { updateIngredients: updateIngredientsEdit } = useGlobalIngredientsEdit(
    mealData.ingredients
  );

  const decimalSeparator = language === 'pl-PL' ? ',' : '.';

  // Get meal name from sessionStorage if flag is set, otherwise use original
  const shouldUseData = sessionStorage.getItem(STORAGE_KEY_SHOULD_USE_DATA) === 'true';
  const mealName = shouldUseData
    ? sessionStorage.getItem('tracker_meal_name') || mealData.name
    : mealData.name;

  // After reset, save original ingredients to sessionStorage
  useEffect(() => {
    const shouldUseData = sessionStorage.getItem(STORAGE_KEY_SHOULD_USE_DATA) === 'true';

    if (!shouldUseData) {
      console.log('shouldResetData - saving ingredients to sessionStorage');
      updateIngredients(mealData.ingredients);
      updateIngredientsEdit(mealData.ingredients);
    }

    if (shouldUseData) {
      // reset tracker_ingredients_edit
      updateIngredientsEdit(localIngredients);
    }
  }, [mealData.ingredients, updateIngredients, updateIngredientsEdit]);

  // Auto-resize textarea for notes field
  useEffect(() => {
    if (!showNotes) return;

    const textarea = textareaRef.current;
    if (!textarea) return;

    const adjustTextareaHeight = () => {
      // Reset height to auto to get the correct scrollHeight
      textarea.style.height = 'auto';
      // Set height based on scrollHeight, with min height
      const minHeight = 60; // Minimum height in pixels
      textarea.style.height = `${Math.max(textarea.scrollHeight, minHeight)}px`;
    };

    // Initial adjustment
    requestAnimationFrame(() => {
      adjustTextareaHeight();
    });

    // Add input event listener directly to textarea
    textarea.addEventListener('input', adjustTextareaHeight);
    textarea.addEventListener('change', adjustTextareaHeight);

    // Also adjust when window is resized
    window.addEventListener('resize', adjustTextareaHeight);

    return () => {
      textarea.removeEventListener('input', adjustTextareaHeight);
      textarea.removeEventListener('change', adjustTextareaHeight);
      window.removeEventListener('resize', adjustTextareaHeight);
    };
  }, [showNotes]);

  // Focus textarea when notes section is opened
  useEffect(() => {
    if (showNotes) {
      // Use setTimeout to ensure textarea is rendered before focusing
      const timer = setTimeout(() => {
        textareaRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [showNotes]);

  // Clear sessionStorage on navigation back or page unload when in backward-navigation mode
  useEffect(() => {
    const clearSessionStorage = () => {
      if (mode === 'backward-navigation') {
        sessionStorage.removeItem('tracker_ingredients');
        sessionStorage.removeItem('tracker_ingredients_edit');
        sessionStorage.removeItem('tracker_multiplier');
        sessionStorage.removeItem('tracker_meal_name');
        sessionStorage.removeItem('tracker_should_use_data_from_session_storage');
        sessionStorage.removeItem('tracker_extracted_meal');
        sessionStorage.removeItem('tracker_extracted_meal_name');
      }
    };

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      clearSessionStorage();
    };

    const handlePageHide = (event: PageTransitionEvent) => {
      clearSessionStorage();
    };

    // Add event listeners
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pagehide', handlePageHide);

    // Cleanup function - also clears storage when component unmounts (e.g., React Router navigation)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handlePageHide);
      clearSessionStorage();
    };
  }, [mode]);

  // Function to calculate total macros from ingredients
  const calculateMacrosFromIngredients = (ingredients: Ingredient[]) => {
    const totalMacro = {
      protein: { grams: 0, percentage: 0 },
      fat: { grams: 0, percentage: 0 },
      carbohydrates: { grams: 0, percentage: 0 },
    };

    let totalKcal = 0;

    ingredients.forEach((ingredient) => {
      totalKcal += ingredient.kcal;

      if (ingredient.macro) {
        totalMacro.protein.grams += ingredient.macro.protein.grams;
        totalMacro.fat.grams += ingredient.macro.fat.grams;
        totalMacro.carbohydrates.grams += ingredient.macro.carbohydrates.grams;
      }
    });

    // Calculate percentages based on total kcal
    if (totalKcal > 0) {
      // Protein: 4 kcal per gram
      totalMacro.protein.percentage = Math.round(
        ((totalMacro.protein.grams * 4) / totalKcal) * 100
      );
      // Fat: 9 kcal per gram
      totalMacro.fat.percentage = Math.round(((totalMacro.fat.grams * 9) / totalKcal) * 100);
      // Carbohydrates: 4 kcal per gram
      totalMacro.carbohydrates.percentage = Math.round(
        ((totalMacro.carbohydrates.grams * 4) / totalKcal) * 100
      );
    }

    return {
      kcal: totalKcal,
      macro: totalMacro,
    };
  };

  // Calculate macros from localIngredients
  const calculatedMealData = useMemo(() => {
    return calculateMacrosFromIngredients(localIngredients);
  }, [localIngredients]);

  const renderMealImage = () => {
    let mealImg;
    if (mealData.preparationImageUrl) {
      mealImg = mealData.preparationImageUrl;
    } else if (mealData.mealType !== undefined) {
      mealImg = mealPlaceholderImage(mealData.mealType, APP_BRAND as string);
    } else {
      // Fallback for cookbook meals or when mealType is not available
      mealImg = mealData.preparationImageUrl;
    }

    const coverImage = cloudflareImage(mealImg, `${APP_BRAND}`, 'cookbookMealDetails');
    return <img src={coverImage} className="img-fluid" width={768} height={768} alt="" />;
  };

  const initialValues = {
    servings: mealData.portions,
    notes: mealData.notes || '',
  };

  const handleSubmit = async (
    values: { servings: number; notes?: string },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resetForm?: (nextState?: any) => void
  ) => {
    setIsSubmitting(true);

    // Map unit names to API format
    const mapUnitToApiFormat = (unit: string): string => {
      const unitMap: { [key: string]: string } = {
        g: 'grams',
        ml: 'milliliters',
        'fl oz': 'fl_oz',
      };
      return unitMap[unit] || unit;
    };

    const requestIngredients = localIngredients.map((ingredient: Ingredient) => {
      const unit =
        ingredient.measurements.length > 1
          ? ingredient.measurements[1].unit
          : ingredient.measurements[0].unit;

      const requestIngredient: {
        name: string;
        brand?: string;
        baseAmount: number;
        baseUnit: string;
        calories: number;
        proteinGrams: number | undefined;
        fatGrams: number | undefined;
        carbsGrams: number | undefined;
        measureId?: number;
        measureAmount?: number;
      } = {
        name: ingredient.name,
        brand: ingredient.brand,
        baseAmount:
          ingredient.measurements.length > 1
            ? ingredient.measurements[1].amount
            : ingredient.measurements[0].amount,
        baseUnit: mapUnitToApiFormat(unit),
        calories: Math.round(ingredient.kcal),
        proteinGrams: ingredient.macro?.protein.grams,
        fatGrams: ingredient.macro?.fat.grams,
        carbsGrams: ingredient.macro?.carbohydrates.grams,
      };

      if (ingredient.measurements.length > 1) {
        requestIngredient.measureId = ingredient.measurements[0].measureId;
        requestIngredient.measureAmount = ingredient.measurements[0].amount;
      }

      return requestIngredient;
    });

    const request = {
      mealId: mealData.id,
      mealName: mealName,
      portions: +values.servings,
      notes: values.notes,
      ingredients: requestIngredients,
    };

    try {
      const response = await editMeal?.(request);

      if (response?.__typename === 'DailyTrackerEditMealSuccess') {
        // Clear sessionStorage
        sessionStorage.removeItem('tracker_ingredients');
        sessionStorage.removeItem('tracker_ingredients_edit');
        sessionStorage.removeItem('tracker_multiplier');
        sessionStorage.removeItem('tracker_meal_name');
        sessionStorage.removeItem('tracker_should_use_data_from_session_storage');
        sessionStorage.removeItem('tracker_extracted_meal');
        sessionStorage.removeItem('tracker_extracted_meal_name');

        setShowNotes(false);
        toastSuccess(<p>{t('meals.notification.editMealConfirmation.content')}</p>);

        // Reset form to clear dirty state
        if (resetForm) {
          resetForm({ values });
        }
      } else {
        toastError();
      }
    } catch (error) {
      toastError();
    } finally {
      setIsSubmitting(false);
    }
  };

  const fakeSubmit = () => {
    // blur input
    inputRef.current?.blur();
  };

  const removeMealHandler = async () => {
    if (!removeMeal) {
      toastError();
      return;
    }
    try {
      setIsRemoving(true);
      const response = await removeMeal(mealData.id);

      if (response?.code === 200) {
        // refresh native tracker data
        callNativeMobileApp({
          action: 'refresh_data',
          payload: {
            module: 'tracker',
            date: date,
          },
        });
        toastSuccess(<p className="w-100">{t('yourMeal.notification.removedFromBase.content')}</p>);
        navigate(-1);
      } else {
        toastError();
      }
    } catch (error) {
      toastError();
    } finally {
      setIsRemoving(false);
    }
  };

  const handleInstaCart = async () => {
    const ingredients: any = [];

    mealData.ingredients.forEach((ingredient: Ingredient) => {
      const measurement = ingredient.measurements[0];
      const measure = `${measurement.amount} ${measurement.text.replace('%s', '')}`;

      ingredients.push(`${measure} ${ingredient.name}`);
    });

    let mealImg = '';

    if (mealData.preparationImageUrl) {
      mealImg = mealData.preparationImageUrl;
    }

    const request = {
      name: mealName,
      ingredients,
      recipe: [],
      image: mealImg,
      description: `Cookbook recipe`,
    };

    setPreloadInstacart(true);

    const response = await fetch(INSTACART_FIREBASE_FUNCTION.toString(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    response.json().then((data) => {
      setPreloadInstacart(false);

      if (data.instacartUrl) {
        const recipeData = encodeURIComponent(data.instacartUrl);
        const instacartUrl = `${INSTACART_URL}?recipeSourceUrl=${recipeData}&utm_source=instacart_growth_partnerships&aff_id=1818&offer_id=1&affiliate_platform=recipe_widget`;

        trackFirebaseEvent('user_action', {
          action: 'tap-instacart_button',
          location: 'Meal_details',
          meals_tab: 'cookbook',
        });

        if (
          mobileApp &&
          mobileApp.os &&
          mobileApp.os.toLowerCase().startsWith('ios') &&
          mobileApp.version &&
          compareVersions(mobileApp.version, '4.0.2')
        ) {
          callNativeMobileApp({
            action: 'deeplink_open',
            payload: {
              url: instacartUrl,
            },
          });
        } else {
          // workound for typescript error
          const win: Window = window;
          win.location = instacartUrl;
        }
      }
    });
  };

  const handleAddMealToTracker = () => {
    navigate(`/meals/user-meals/module/tracker/add-meal/${mealData.id}`);
  };

  return (
    <>
      <Layout>
        <TitleBar showBackBtn={true}>{t('tracker.mealDetails.navbarTitle')}</TitleBar>

        <Formik
          onSubmit={fakeSubmit}
          initialValues={initialValues}
          validationSchema={validationSchema}
        >
          {({ isValid, values, dirty, resetForm }) => {
            // Helper function to format macro values
            const formatMacroDisplay = (originalGrams: number, originalPercentage: number) => {
              const multipliedGrams = Math.round(originalGrams * values.servings);

              // If multiplied grams is 0 but original percentage > 0, show "<1g"
              if (multipliedGrams === 0 && originalPercentage > 0) {
                return '<1';
              }

              return multipliedGrams;
            };

            const MULTIPLIER_STORAGE_KEY = 'tracker_multiplier';
            // save servings to sessionStorage
            sessionStorage.setItem(
              MULTIPLIER_STORAGE_KEY,
              Math.max(values.servings, 0.001).toString()
            );

            // Calculate multiplied values based on servings
            const multipliedKcal = Math.round(calculatedMealData.kcal * values.servings);
            const multipliedMacro = calculatedMealData.macro
              ? {
                  protein: {
                    grams: formatMacroDisplay(
                      calculatedMealData.macro.protein.grams,
                      calculatedMealData.macro.protein.percentage
                    ),
                  },
                  fat: {
                    grams: formatMacroDisplay(
                      calculatedMealData.macro.fat.grams,
                      calculatedMealData.macro.fat.percentage
                    ),
                  },
                  carbohydrates: {
                    grams: formatMacroDisplay(
                      calculatedMealData.macro.carbohydrates.grams,
                      calculatedMealData.macro.carbohydrates.percentage
                    ),
                  },
                }
              : {
                  protein: { grams: 0 },
                  fat: { grams: 0 },
                  carbohydrates: { grams: 0 },
                };

            let showInstacartButton = false;
            if (getCountry() === 'Canada' || getCountry() === 'United States of America') {
              showInstacartButton = true;
            }

            return (
              <Form>
                <div className="page-template-user-meals">
                  <Container className="py-md-4 full-height4">
                    <Row>
                      <Col xl={{ size: 10, offset: 1 }}>
                        <div className="page-container">
                          <div className="meal-details">
                            <Row>
                              <Col md="6">
                                <div className="meal-image-video">
                                  {renderMealImage()}

                                  <div className="meal-info2 d-md-none">
                                    <Container>
                                      <h1 className="meal-title title-large semi-bold white mb-0">
                                        {mealName}
                                      </h1>
                                    </Container>
                                  </div>
                                </div>
                              </Col>
                              <Col md="6">
                                <div className="instacart-container d-md-none">
                                  {showInstacartButton && (
                                    <div className="text-center">
                                      <Button
                                        color="instacart"
                                        className="w-100 w-md-auto"
                                        onClick={handleInstaCart}
                                        disabled={preloadInstacart}
                                      >
                                        {preloadInstacart && (
                                          <span
                                            className="spinner-border spinner-border-sm me-2"
                                            role="status"
                                            aria-hidden="true"
                                          />
                                        )}
                                        {t('orderIngredients.instacart.button')}{' '}
                                        <img
                                          src={instacartLogo}
                                          style={{
                                            verticalAlign: 'baseline',
                                            height: '13px',
                                          }}
                                          alt="instacart"
                                        />
                                      </Button>
                                    </div>
                                  )}
                                </div>

                                <div className="meal-info">
                                  <div className="d-none d-md-block">
                                    <h1 className="meal-title title-large semi-bold mb-xl">
                                      {mealName}
                                    </h1>
                                  </div>

                                  <div className="grey-box mb-3">
                                    {t('mealDetails.info.servings.title')}{' '}
                                    <strong className="float-end">{mealData.portions}</strong>
                                  </div>

                                  <Button
                                    color="primary"
                                    size="sm"
                                    className="w-100 d-none d-md-block tracker"
                                    onClick={handleAddMealToTracker}
                                  >
                                    <img
                                      src={addToTrackerIcon}
                                      alt={t('tracker.meal.addToTrackerButton')}
                                      className="me-2"
                                    />
                                    {t('tracker.meal.addToTrackerButton')}
                                  </Button>

                                  <div className="d-none d-md-block">
                                    <div className="table-wrapper">
                                      <Table>
                                        <thead>
                                          <tr>
                                            <th></th>
                                            <th>{t('mealDetails.macrosTable.allServings')}</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          <tr>
                                            <th scope="row">
                                              {t('mealDetails.macros.calories.title')}
                                            </th>
                                            <td>{formatCalories(multipliedKcal)} kcal</td>
                                          </tr>
                                          <tr>
                                            <th scope="row">
                                              {t('mealDetails.macrosTable.proteins.title')}
                                            </th>

                                            <td>{multipliedMacro.protein.grams} g</td>
                                          </tr>
                                          <tr>
                                            <th scope="row">
                                              {t('mealDetails.macrosTable.fats.title')}
                                            </th>
                                            <td>{multipliedMacro.fat.grams} g</td>
                                          </tr>
                                          <tr>
                                            <th>{t('mealDetails.macrosTable.carbs.title')}</th>
                                            <td>{multipliedMacro.carbohydrates.grams} g</td>
                                          </tr>
                                        </tbody>
                                      </Table>
                                    </div>
                                  </div>

                                  {showInstacartButton && (
                                    <div className="text-center d-none d-md-block">
                                      <Button
                                        color="instacart"
                                        className="w-100 w-md-auto"
                                        onClick={handleInstaCart}
                                        disabled={preloadInstacart}
                                      >
                                        {preloadInstacart && (
                                          <span
                                            className="spinner-border spinner-border-sm me-2"
                                            role="status"
                                            aria-hidden="true"
                                          />
                                        )}
                                        {t('orderIngredients.instacart.button')}{' '}
                                        <img
                                          src={instacartLogo}
                                          style={{
                                            verticalAlign: 'baseline',
                                            height: '13px',
                                          }}
                                          alt="instacart"
                                        />
                                      </Button>
                                    </div>
                                  )}

                                  {FRISCO && <FriscoButton meal={mealData} publicName={mealName} />}
                                </div>
                              </Col>
                            </Row>
                          </div>
                        </div>

                        <div className="tracker-ingredients-list">
                          <p className="text-text-main-color semi-bold mb-m d-flex justify-content-between">
                            {t('tracker.logMeal.ingredients.title')}

                            {mode === 'backward-navigation' ? (
                              <Link
                                to={`edit-meal`}
                                className="small"
                                state={{
                                  ingredients: localIngredients,
                                  mealName: mealName,
                                  timestamp: Date.now(), // Force re-render
                                }}
                              >
                                <img src={editPrimaryIcon} alt="edit" className="me-1" />
                                {t('tracker.logMeal.ingredients.editButton')}
                              </Link>
                            ) : (
                              <Button
                                color="link"
                                size="sm"
                                className="p-0"
                                onClick={() => navigate(-1)}
                              >
                                <img src={editPrimaryIcon} alt="edit" className="me-1" />
                                {t('tracker.logMeal.ingredients.editButton')}
                              </Button>
                            )}
                          </p>

                          <ul className="tracker-ingredients-list-items">
                            {localIngredients.map((ingredient: Ingredient, index: number) => {
                              return (
                                <li
                                  key={ingredient.id || `ingredient_${index}`}
                                  className="tracker-ingredient-item"
                                >
                                  <div className="d-flex justify-content-between">
                                    <span className="small text-text-main-color semi-bold text-truncate-2-line">
                                      {ingredient.name}
                                      {ingredient.brand && ` (${ingredient.brand})`}
                                    </span>
                                    <span className="small text-text-main-color semi-bold">
                                      {ingredient.measurements &&
                                        ingredient.measurements.length > 1 && (
                                          <MeasurementUnits
                                            measurements={[ingredient.measurements[1]]}
                                            multiplier={values.servings}
                                          />
                                        )}

                                      {ingredient.measurements &&
                                        (ingredient.measurements[0].unit === 'ml' ||
                                          ingredient.measurements[0].unit === 'milliliters' ||
                                          ingredient.measurements[0].unit === 'cl' ||
                                          ingredient.measurements[0].unit === 'oz' ||
                                          ingredient.measurements[0].unit === 'fl oz' ||
                                          ingredient.measurements[0].unit === 'fl_oz' ||
                                          ingredient.measurements[0].unit === 'g' ||
                                          ingredient.measurements[0].unit === 'grams') && (
                                          <MeasurementUnits
                                            measurements={[ingredient.measurements[0]]}
                                            multiplier={values.servings}
                                          />
                                        )}
                                    </span>
                                  </div>
                                  <div className="d-flex justify-content-between">
                                    <span className="description text-text-variant-2-color">
                                      {formatCalories(ingredient.kcal * values.servings)} kcal
                                      <span className="d-inline-block ms-3">
                                        {t('summary.macros.proteinsShort')}{' '}
                                        {(
                                          Math.round(
                                            (ingredient.macro?.protein?.grams ?? 0) *
                                              values.servings *
                                              10
                                          ) / 10
                                        )
                                          .toString()
                                          .replace('.', decimalSeparator)}
                                        g
                                      </span>
                                      <span className="d-inline-block ms-s">
                                        {t('summary.macros.fatsShort')}{' '}
                                        {(
                                          Math.round(
                                            (ingredient.macro?.fat?.grams ?? 0) *
                                              values.servings *
                                              10
                                          ) / 10
                                        )
                                          .toString()
                                          .replace('.', decimalSeparator)}
                                        g
                                      </span>
                                      <span className="d-inline-block ms-s">
                                        {t('summary.macros.carbsShort')}{' '}
                                        {(
                                          Math.round(
                                            (ingredient.macro?.carbohydrates?.grams ?? 0) *
                                              values.servings *
                                              10
                                          ) / 10
                                        )
                                          .toString()
                                          .replace('.', decimalSeparator)}
                                        g
                                      </span>
                                    </span>
                                    <span className="description text-text-variant-2-color">
                                      {ingredient.measurements &&
                                        ingredient.measurements[0].unit !== 'ml' &&
                                        ingredient.measurements[0].unit !== 'milliliters' &&
                                        ingredient.measurements[0].unit !== 'cl' &&
                                        ingredient.measurements[0].unit !== 'oz' &&
                                        ingredient.measurements[0].unit !== 'fl oz' &&
                                        ingredient.measurements[0].unit !== 'fl_oz' &&
                                        ingredient.measurements[0].unit !== 'g' &&
                                        ingredient.measurements[0].unit !== 'grams' && (
                                          <>
                                            <span className="me-1">≈</span>
                                            <MeasurementUnits
                                              measurements={[ingredient.measurements[0]]}
                                              multiplier={values.servings}
                                            />
                                          </>
                                        )}
                                    </span>
                                  </div>
                                </li>
                              );
                            })}
                          </ul>

                          {mealData.sourceType === 'ai_description' && (
                            <div className="d-flex align-items-center mt-xl ">
                              <img
                                src={aiBadgeTiny}
                                alt=""
                                className="me-s"
                                style={{ width: '39px' }}
                              />
                              <p className="mb-0 description text-text-variant-color">
                                {t('tracker.aiSignature')}
                              </p>
                              <img
                                src={infoIcon}
                                className="ms-s"
                                style={{ width: '20px' }}
                                onClick={toggleAiModal}
                                alt=""
                              />
                            </div>
                          )}

                          <div className="user-notes">
                            <p className="text-text-main-color semi-bold d-flex justify-content-between w-100">
                              {t('mealDetails.notesBox.title')}

                              {showNotes && (
                                <Button
                                  color="link"
                                  size="sm"
                                  className="p-0"
                                  onClick={() => setShowNotes(false)}
                                >
                                  {t('mealDetails.notesBox.confirmButton')}
                                </Button>
                              )}

                              {values.notes !== '' && !showNotes && (
                                <Button
                                  color="link"
                                  size="sm"
                                  className="p-0"
                                  onClick={() => setShowNotes(true)}
                                >
                                  {t('mealDetails.notesBox.editButton')}
                                </Button>
                              )}
                            </p>

                            {showNotes ? (
                              <Input
                                type="textarea"
                                name="notes"
                                className="w-100"
                                innerRef={textareaRef}
                                style={{ resize: 'none', overflow: 'hidden', minHeight: '60px' }}
                              />
                            ) : mealData.notes !== '' || values.notes !== '' ? (
                              <p className="text-text-variant-color text-prewrap">{values.notes}</p>
                            ) : (
                              <button className="add-note w-100" onClick={() => setShowNotes(true)}>
                                <img src={addPlusIcon} alt="add" />
                                <span>{t('mealDetails.notesBox.addButton')}</span>
                              </button>
                            )}
                          </div>

                          <div className="d-md-none page-container">
                            <div className="meal-details">
                              <div className="table-wrapper mx-0">
                                <Table>
                                  <thead>
                                    <tr>
                                      <th></th>
                                      <th>{t('mealDetails.macrosTable.allServings')}</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    <tr>
                                      <th scope="row">{t('mealDetails.macros.calories.title')}</th>
                                      <td>{formatCalories(multipliedKcal)} kcal</td>
                                    </tr>
                                    <tr>
                                      <th scope="row">
                                        {t('mealDetails.macrosTable.proteins.title')}
                                      </th>

                                      <td>{multipliedMacro.protein.grams} g</td>
                                    </tr>
                                    <tr>
                                      <th scope="row">{t('mealDetails.macrosTable.fats.title')}</th>
                                      <td>{multipliedMacro.fat.grams} g</td>
                                    </tr>
                                    <tr>
                                      <th>{t('mealDetails.macrosTable.carbs.title')}</th>
                                      <td>{multipliedMacro.carbohydrates.grams} g</td>
                                    </tr>
                                  </tbody>
                                </Table>
                              </div>
                            </div>
                          </div>

                          <Button
                            color="link"
                            className="w-100 mt-md-3"
                            onClick={removeMealHandler}
                            disabled={isRemoving}
                          >
                            {isRemoving ? (
                              <>
                                <Spinner size="sm" />
                                <span className="ms-2">{t('data-processing')}</span>
                              </>
                            ) : (
                              <>
                                <img src={yourMealsRemoveIcon} alt="delete" className="me-2" />
                                <span className="text-text-error-color">
                                  {t('yourMeal.removeButton')}
                                </span>
                              </>
                            )}
                          </Button>
                        </div>
                      </Col>
                    </Row>
                  </Container>

                  <div className="sticky-footer">
                    <Container className="px-3">
                      <Row>
                        <Col xs="12" md={{ size: 4, offset: 4 }}>
                          {shouldUseDataFromSessionStorage || dirty ? (
                            <Button
                              type="button"
                              onClick={() => handleSubmit(values, resetForm)}
                              color="primary"
                              className="w-100"
                              disabled={isSubmitting || !isValid}
                            >
                              {isSubmitting ? (
                                <>
                                  <Spinner />
                                  <span className="ms-2">{t('data-processing')}</span>
                                </>
                              ) : (
                                t('tracker.mealDetails.saveButton')
                              )}
                            </Button>
                          ) : (
                            <Button
                              color="primary"
                              className="w-100 px-3 tracker"
                              onClick={handleAddMealToTracker}
                            >
                              <img
                                src={addToTrackerIcon}
                                alt={t('tracker.meal.addToTrackerButton')}
                                className="me-2"
                              />
                              {t('tracker.meal.addToTrackerButton')}
                            </Button>
                          )}
                        </Col>
                      </Row>
                    </Container>
                  </div>
                </div>
              </Form>
            );
          }}
        </Formik>
      </Layout>

      <Modal isOpen={showAiModal} toggle={toggleAiModal} centered size="md">
        <ModalHeader toggle={toggleAiModal} />
        <ModalBody className="pt-0">
          <h2 className="title-medium semi-bold">{t('tracker.aiSignature.popup.title')}</h2>
          <p>{t('tracker.aiSignature.popup.description')}</p>

          <Button color="primary" className="w-100" onClick={toggleAiModal}>
            {t('tracker.aiSignature.popup.button')}
          </Button>
        </ModalBody>
      </Modal>
    </>
  );
};

export default withMobileAppMode(TrackerEditMeal);
